// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, IconButton, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';

// Governance for KEYS, as opposed to values.
//
// A field definition changes the shape of every fact sheet of its entity type,
// so it is not something an individual should be able to introduce on their own
// — but the previous behaviour was worse than either extreme: the control was
// offered to everyone, the write was admin-only, and a non-admin's field landed
// in their browser and nowhere else. It looked like it had worked. This screen
// turns that into an explicit request that somebody answers.

const ENTITY_TYPES = ['Application', 'ITComponent', 'Interface', 'Deployment', 'BusinessCapability', 'Project'];
const DATA_TYPES = ['text', 'textarea', 'number', 'currency', 'boolean', 'date', 'percent', 'select', 'rating', 'url'];

const BLANK = {
  entityType: 'Application', key: '', label: '', dataType: 'text',
  section: 'Custom Fields', scope: 'tenant', justification: '',
};

// A key becomes a column name everywhere it is read, so it is constrained here
// as well as being rejected downstream — the point is to say why, at the moment
// of typing, rather than to be the only guard.
const keyIsValid = (k) => /^[a-z][a-zA-Z0-9_]*$/.test(k);

const STATUS_COLOR = { pending: 'warning', approved: 'success', rejected: 'default' };

export default function FieldRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [ask, setAsk] = useState(null); // the new-request form
  const [decide, setDecide] = useState(null); // { row, status, note }

  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');
  const iAmAdmin = iAmMaster || (me && me.roleId === 'admin');

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const list = await api('/field-requests');
      if (!Array.isArray(list)) throw new Error('The field-request service returned an unexpected response.');
      setRows(list);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );
  const pendingCount = useMemo(() => rows.filter((r) => r.status === 'pending').length, [rows]);

  const submitAsk = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api('/field-requests', { method: 'POST', body: JSON.stringify(ask) });
      setMsg('Your request has been filed. An administrator will decide on it.');
      setAsk(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const submitDecide = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api('/field-requests', {
        method: 'PUT',
        body: JSON.stringify({ id: decide.row.id, status: decide.status, note: decide.note }),
      });
      setMsg(decide.status === 'approved'
        ? `Approved — "${decide.row.label}" now exists on every ${decide.row.entity_type} fact sheet in scope.`
        : `Rejected "${decide.row.label}".`);
      setDecide(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // A global key lands on the shared baseline and is therefore inherited by
  // every client, so a tenant admin may not approve one even though they can
  // see it. The server refuses this too; here it just stops the dead click.
  const mayDecide = (r) => iAmAdmin && !(r.scope === 'global' && !iAmMaster);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#021F4B' }}>Field requests</Typography>
        {!!pendingCount && <Chip size="small" color="warning" label={`${pendingCount} awaiting a decision`} />}
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)}>
          <ToggleButton value="pending" sx={{ textTransform: 'none' }}>Pending</ToggleButton>
          <ToggleButton value="approved" sx={{ textTransform: 'none' }}>Approved</ToggleButton>
          <ToggleButton value="rejected" sx={{ textTransform: 'none' }}>Rejected</ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none' }}>All</ToggleButton>
        </ToggleButtonGroup>
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        {!iAmMaster && (
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setAsk({ ...BLANK })}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            Request a field
          </Button>
        )}
      </Stack>

      <Typography variant="body2" sx={{ color: '#5C6B7A', mb: 2 }}>
        Adding a <b>field</b> changes what every fact sheet of that type is expected to hold, so it
        goes through here. Adding a <b>value</b> to an existing list does not — do that under
        Lists &amp; values.
        {iAmMaster && ' As an Optimum master you create fields outright rather than requesting them.'}
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                <TableCell>Field</TableCell>
                <TableCell>Applies to</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Requested by</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{r.label}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#9AA6B2', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                      {r.key}
                      {' · '}
                      {r.data_type}
                    </Typography>
                    {r.justification && (
                      <Typography sx={{ fontSize: 12, color: '#5C6B7A', mt: 0.5 }}>{r.justification}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{r.entity_type}</TableCell>
                  <TableCell>
                    {r.scope === 'global'
                      ? <Chip size="small" label="ALL CLIENTS" sx={{ bgcolor: '#021F4B', color: '#fff', height: 20 }} />
                      : <Chip size="small" variant="outlined" label={r.tenant_id} sx={{ height: 20 }} />}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>
                    {r.requested_by}
                    <Typography sx={{ fontSize: 11, color: '#9AA6B2' }}>
                      {r.requested_at ? String(r.requested_at).slice(0, 10) : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={STATUS_COLOR[r.status] || 'default'} sx={{ height: 20 }} />
                    {r.decided_by && (
                      <Typography sx={{ fontSize: 11, color: '#9AA6B2' }}>
                        by
                        {' '}
                        {r.decided_by}
                      </Typography>
                    )}
                    {r.decision_note && (
                      <Typography sx={{ fontSize: 11.5, color: '#5C6B7A' }}>{r.decision_note}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {r.status !== 'pending' ? (
                      <Typography sx={{ fontSize: 11.5, color: '#9AA6B2' }}>closed</Typography>
                    ) : mayDecide(r) ? (
                      <>
                        <Tooltip title="Approve — creates the field">
                          <IconButton size="small" color="success"
                            onClick={() => setDecide({ row: r, status: 'approved', note: '' })}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton size="small"
                            onClick={() => setDecide({ row: r, status: 'rejected', note: '' })}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title={r.scope === 'global'
                        ? 'A field for every client can only be approved by an Optimum master.'
                        : 'Only an administrator can decide on a request.'}>
                        <LockIcon fontSize="small" sx={{ color: '#C8D0D8' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!visible.length && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                    {filter === 'pending' ? 'Nothing is waiting on a decision.' : 'No requests here.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={!!ask} onClose={() => !busy && setAsk(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: '#021F4B' }}>Request a new field</DialogTitle>
        {ask && (
          <DialogContent dividers>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField select fullWidth size="small" label="Applies to" value={ask.entityType}
                onChange={(e) => setAsk({ ...ask, entityType: e.target.value })}>
                {ENTITY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField select fullWidth size="small" label="Type" value={ask.dataType}
                onChange={(e) => setAsk({ ...ask, dataType: e.target.value })}>
                {DATA_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField fullWidth size="small" label="Label" sx={{ mb: 2 }} value={ask.label}
              onChange={(e) => setAsk({ ...ask, label: e.target.value })}
              helperText="What people will see on the fact sheet, e.g. Disaster Recovery Tier." />
            <TextField fullWidth size="small" label="Key" sx={{ mb: 2 }} value={ask.key}
              onChange={(e) => setAsk({ ...ask, key: e.target.value })}
              error={!!ask.key && !keyIsValid(ask.key)}
              helperText={ask.key && !keyIsValid(ask.key)
                ? 'Start with a lowercase letter, then letters, numbers or underscores only.'
                : 'The stored name. Cannot be changed later without a migration, e.g. drTier.'} />
            <TextField fullWidth size="small" label="Why is it needed?" multiline minRows={2} sx={{ mb: 2 }}
              value={ask.justification} onChange={(e) => setAsk({ ...ask, justification: e.target.value })}
              helperText="The person deciding will mostly be going on this." />
            <TextField select fullWidth size="small" label="Scope" value={ask.scope}
              onChange={(e) => setAsk({ ...ask, scope: e.target.value })}>
              <MenuItem value="tenant">Just our workspace</MenuItem>
              <MenuItem value="global">Every client (needs Optimum approval)</MenuItem>
            </TextField>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setAsk(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submitAsk}
            disabled={busy || !ask || !ask.label || !keyIsValid(ask.key || '')}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? 'Filing…' : 'Send request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!decide} onClose={() => !busy && setDecide(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, color: '#021F4B' }}>
          {decide && decide.status === 'approved' ? 'Approve this field?' : 'Reject this request?'}
        </DialogTitle>
        {decide && (
          <DialogContent dividers>
            <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
              <b>{decide.row.label}</b>
              {' on every '}
              <b>{decide.row.entity_type}</b>
              {decide.row.scope === 'global'
                ? ' fact sheet belonging to every client.'
                : ` fact sheet in ${decide.row.tenant_id}.`}
            </Typography>
            {decide.status === 'approved' && decide.row.scope === 'global' && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: 12.5 }}>
                This publishes the field to the shared baseline. Every client inherits it, and their
                completion scores change accordingly.
              </Alert>
            )}
            <TextField fullWidth size="small" multiline minRows={2}
              label={decide.status === 'approved' ? 'Note (optional)' : 'Reason'}
              value={decide.note} onChange={(e) => setDecide({ ...decide, note: e.target.value })}
              helperText={decide.status === 'approved'
                ? 'Kept with the request as the record of who agreed and why.'
                : 'The requester sees this. Say what would need to change.'} />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDecide(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submitDecide}
            disabled={busy || !decide || (decide.status === 'rejected' && !decide.note.trim())}
            sx={decide && decide.status === 'approved'
              ? { bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }
              : { bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? 'Saving…' : (decide && decide.status === 'approved' ? 'Approve & create' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
